import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Cookie, X, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';

const COOKIE_CONSENT_KEY = 'apisai_cookie_consent';

export default function CookieConsent() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to not block initial render
      setTimeout(() => setShow(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    window.dispatchEvent(new Event('apisai-cookie-consent'));
    setShow(false);
  };

  const handleAcceptSelected = () => {
    const consent = {
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    window.dispatchEvent(new Event('apisai-cookie-consent'));
    setShow(false);
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    window.dispatchEvent(new Event('apisai-cookie-consent'));
    setShow(false);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[100]"
      >
        <div className="w-full bg-[#0c0c18]/95 backdrop-blur-md border-t border-orange-500/20 shadow-2xl shadow-orange-500/10">
          {/* Main banner */}
          {!showSettings ? (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-4">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 bg-gradient-to-br from-orange-500/20 to-orange-400/20 rounded-lg flex items-center justify-center flex-shrink-0 border border-orange-500/30">
                    <Cookie className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400" />
                  </div>
                  <p className="text-zinc-300 text-xs sm:text-sm leading-tight">
                    {t('cookies.description')}{' '}
                    <Link to="/polityka-cookies" className="text-orange-400 hover:text-orange-300 hover:underline transition-colors">
                      {t('cookies.learnMore')}
                    </Link>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    onClick={handleAcceptAll}
                    className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 rounded-full px-4 sm:px-5 text-xs sm:text-sm border-0 h-8 sm:h-9"
                    data-testid="cookie-accept-all"
                  >
                    {t('cookies.acceptAll')}
                  </Button>
                  <Button
                    onClick={handleRejectAll}
                    variant="outline"
                    className="border-white/20 text-gray-300 hover:bg-white/10 hover:border-orange-500/30 rounded-full px-4 sm:px-5 text-xs sm:text-sm h-8 sm:h-9"
                    data-testid="cookie-reject"
                  >
                    {t('cookies.onlyNecessary')}
                  </Button>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="text-sm text-zinc-400 hover:text-orange-300 items-center gap-1 transition-colors hidden sm:flex"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Settings panel */
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">{t('cookies.settings')}</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-zinc-400 hover:text-orange-300 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                {/* Necessary */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-orange-500/20">
                  <div>
                    <p className="font-medium text-white text-sm">{t('cookieSettings.necessary', 'Necessary')}</p>
                    <p className="text-xs text-zinc-400">{t('cookieSettings.necessaryDesc', 'Required for the site to work')}</p>
                  </div>
                  <div className="w-10 h-5 bg-gradient-to-r from-orange-600 to-orange-500 rounded-full flex items-center justify-end px-0.5 flex-shrink-0 ml-2">
                    <div className="w-3.5 h-3.5 bg-white rounded-full" />
                  </div>
                </div>

                {/* Analytics */}
                <label className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-orange-500/20 cursor-pointer hover:bg-orange-500/10 transition-colors">
                  <div>
                    <p className="font-medium text-white text-sm">{t('cookieSettings.analytics', 'Analytics')}</p>
                    <p className="text-xs text-zinc-400">{t('cookieSettings.analyticsDesc', 'Help understand how you use the site')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                    className="w-4 h-4 rounded border-orange-500/30 bg-white/10 text-orange-500 focus:ring-orange-500 flex-shrink-0 ml-2"
                  />
                </label>

                {/* Marketing */}
                <label className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-orange-500/20 cursor-pointer hover:bg-orange-500/10 transition-colors">
                  <div>
                    <p className="font-medium text-white text-sm">{t('cookieSettings.marketing', 'Marketing')}</p>
                    <p className="text-xs text-zinc-400">{t('cookieSettings.marketingDesc', 'Used for ad personalization')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                    className="w-4 h-4 rounded border-orange-500/30 bg-white/10 text-orange-500 focus:ring-orange-500 flex-shrink-0 ml-2"
                  />
                </label>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  onClick={handleAcceptSelected}
                  className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 rounded-full px-5 border-0 h-9 text-sm"
                >
                  {t('common.save')}
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="border-white/20 text-gray-300 hover:bg-white/10 hover:border-orange-500/30 rounded-full px-5 h-9 text-sm"
                >
                  {t('cookies.acceptAll')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
